/**
 * Application answers — the gate.
 *
 * These go out under the user's name on real applications, so the interface
 * has two jobs beyond collecting data:
 *
 *   - Make "not answered" a visible, selectable state. Voluntary
 *     self-identification is voluntary; a form that only offers yes and no
 *     quietly converts a declined question into an answer.
 *   - Make confirmation a separate, deliberate act, and say plainly that
 *     editing undoes it. Nothing is submitted on someone's behalf that they
 *     have not seen in its current form.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { NextStep, type NextStepInfo } from "./NextStep";
import { QuestionBank } from "./Bank";
import { TextField, YesNo, RadioGroup, MoneyField, TagField, ErrorList } from "./fields";
import { US_STATES, splitCityState, joinCityState } from "./usStates";

export interface Answers {
  legalFirstName: string | null;
  legalLastName: string | null;
  email: string | null;
  phone: string | null;
  homeCity: string | null;
  homeCountry: string | null;
  homePostalCode: string | null;
  homeStreet: string | null;
  workAuthorizedUs: boolean | null;
  requiresSponsorship: boolean | null;
  citizenship: string | null;
  clearance: string | null;
  nonCompete: boolean | null;
  smsOptIn: boolean | null;
  openToOffice: boolean | null;
  yearsExperience: number | null;
  experienceAreas: Array<{ area: string; years: number }>;
  industries: string[];
  skills: string[];
  acceptConfidentiality: boolean;
  acceptAgreements: boolean;
  veteranStatus: string | null;
  disabilityStatus: string | null;
  felonyDisclosure: string | null;
  felonyConviction: boolean | null;
  priorEmployeeOf: string[];
  interviewedAt: string[];
  highestEducation: string | null;
  willingToRelocate: boolean | null;
  earliestStartDate: string | null;
  desiredCompCents: number | null;
  currentCompany: string | null;
  currentTitle: string | null;
  acceptStandardConsents: boolean;
  letModelAnswer: boolean;
  confirmedAt: string | null;
}

interface Payload {
  answers: Answers;
  missing: string[];
  conflicts: string[];
  ready: boolean;
}

const VETERAN = [
  { value: "decline", label: "I decline to answer", note: "A complete answer. Employers may not penalise it." },
  { value: "not_a_veteran", label: "I am not a protected veteran" },
  { value: "veteran", label: "I am a veteran" },
  { value: "protected_veteran", label: "I am a protected veteran" },
];

const DISABILITY = [
  { value: "decline", label: "I decline to answer", note: "A complete answer. Employers may not penalise it." },
  { value: "no", label: "No, I do not have a disability" },
  { value: "yes", label: "Yes, I have or have had a disability" },
];

const CITIZENSHIP = [
  { value: "us_citizen", label: "U.S. citizen" },
  { value: "permanent_resident", label: "Permanent resident (green card)" },
  { value: "other", label: "Something else", note: "Left for you on every form that asks: a visa's own wording is yours to choose." },
];

const CLEARANCE = [
  { value: "none", label: "No, never held one" },
  { value: "previously_held", label: "Held one before, not active now" },
  { value: "active", label: "Yes, active now" },
];

/**
 * Years by area: a short list of {area, years} rows. Each area answers every
 * "N years of experience in <area>" question that uses its words, so the
 * names are the seeker's own and kept to what they would say yes to.
 */
function AreasField({ values, onChange }: { values: Array<{ area: string; years: number }>; onChange: (v: Array<{ area: string; years: number }>) => void }) {
  const [area, setArea] = useState("");
  const [years, setYears] = useState("");
  const add = () => {
    const a = area.replace(/\s+/g, " ").trim();
    const y = Number(years);
    if (!a || !Number.isInteger(y) || y < 0 || y > 60) return;
    if (values.some((v) => v.area.toLowerCase() === a.toLowerCase())) { setArea(""); setYears(""); return; }
    onChange([...values, { area: a, years: y }]);
    setArea(""); setYears("");
  };
  return (
    <div className="field">
      <span className="field-label">Years by area</span>
      <p className="muted small field-hint">
        Each area answers every “N years of experience in …” question that uses its words — “information security” answers “3 years as an Information Security Analyst”; a single word like “security” answers anything that says security. Keep each to what you would answer yes to under that name.
      </p>
      {values.length > 0 && (
        <ul className="areas small">
          {values.map((v, i) => (
            <li key={`${v.area}-${i}`}>
              <span>{v.area}</span> <span className="muted">— {v.years} year{v.years === 1 ? "" : "s"}</span>
              <button type="button" className="tag-x" aria-label={`Remove ${v.area}`} onClick={() => onChange(values.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="areas-add">
        <input className="input" value={area} placeholder="information security" aria-label="Area"
               onChange={(e) => setArea(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <input className="input" type="number" min={0} max={60} value={years} placeholder="years" aria-label="Years"
               onChange={(e) => setYears(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <button type="button" className="btn ghost small" onClick={add} disabled={!area.trim() || years === ""}>Add</button>
      </div>
    </div>
  );
}

export default function AnswersScreen({ onSaved, onBack, next, onGo }: { onSaved: () => void; onBack: () => void; next: NextStepInfo; onGo: (go: string) => void }) {
  const [state, setState] = useState<Payload | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"save" | "confirm" | false>(false);
  /** The answers as the server last had them, so the screen can say when what is on it is not yet saved. */
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [justConfirmed, setJustConfirmed] = useState(false);

  /** Separate from `state` so a failed load shows the error rather than
   *  sitting on "Loading…" forever. See the same note in Preferences.tsx. */
  const load = useCallback(() => {
    setLoading(true);
    setErrors([]);
    api<Payload>("/api/answers")
      .then((p) => { setState(p); setSavedSnapshot(JSON.stringify(p.answers)); })
      .catch((e) => setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  if (loading) return <p className="muted">Loading…</p>;

  if (!state) {
    return (
      <>
        <header className="bar">
          <h1 className="tight">Couldn't load your answers</h1>
          <button className="btn ghost small" onClick={onBack}>Back</button>
        </header>
        <ErrorList errors={errors} />
        <p className="muted small">
          If this says 404, the API server is running older code — restart{" "}
          <code>wrangler dev</code>.
        </p>
        <div className="row"><button className="btn" onClick={load}>Try again</button></div>
      </>
    );
  }

  const a = state.answers;

  const set = <K extends keyof Answers>(k: K, v: Answers[K]) =>
    setState({ ...state, answers: { ...a, [k]: v } });

  const dirty = JSON.stringify(a) !== savedSnapshot;

  const run = async (what: "save" | "confirm", fn: () => Promise<Payload>) => {
    setBusy(what);
    setErrors([]);
    try {
      const p = await fn();
      setState(p);
      setSavedSnapshot(JSON.stringify(p.answers));
      setJustConfirmed(what === "confirm" && !!p.answers.confirmedAt);
      onSaved();
    } catch (e) {
      setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]);
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run("save", () => api<Payload>("/api/answers", { method: "PUT", body: JSON.stringify(a) }));

  // Confirming saves first: what is confirmed is always what is on the
  // screen, never an older copy of it.
  const confirm = () =>
    run("confirm", async () => {
      const saved = await api<Payload>("/api/answers", { method: "PUT", body: JSON.stringify(a) });
      if (saved.missing.length > 0 || saved.conflicts.length > 0) return saved;
      return api<Payload>("/api/answers/confirm", { method: "POST" });
    });

  return (
    <>
      <header className="bar">
        <div>
          <h1 className="tight">Your application answers</h1>
          <p className="muted small">
            These go out under your name. We never guess any of them.
          </p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      {a.confirmedAt && !dirty ? (
        <p className="badge">Confirmed</p>
      ) : dirty ? (
        <p className="notice small">
          You have changes that are not saved yet. <a href="#answers-actions">Save and confirm at the bottom of this page</a> — until then, the saved answers stand.
        </p>
      ) : (
        <p className="notice small">
          Not confirmed yet. Nothing is sent on your behalf until you confirm at
          the bottom of this page.
        </p>
      )}

      <h2>Who you are</h2>
      <div className="pair">
        <TextField label="Legal first name" value={a.legalFirstName}
                   onChange={(v) => set("legalFirstName", v)} />
        <TextField label="Legal last name" value={a.legalLastName}
                   onChange={(v) => set("legalLastName", v)} />
      </div>
      <TextField label="Email" type="email" value={a.email}
                 hint="Where employers will reply. It does not have to be your sign-in address."
                 onChange={(v) => set("email", v)} />
      <TextField label="Phone" type="tel" value={a.phone} onChange={(v) => set("phone", v)} />

      <h3>Where you live</h3>
      <p className="muted small">
        Most forms ask the city and state; some a full address. The state matters on its own — state boxes, location pickers and your time zone all read it.
      </p>
      <TextField label="Street address" value={a.homeStreet} placeholder="123 Main St"
                 hint="Optional. For the forms that ask a full address; left blank, those are left for you."
                 onChange={(v) => set("homeStreet", v)} />
      <div className="triple">
        <TextField label="City" value={splitCityState(a.homeCity).city} placeholder="Nashville"
                   onChange={(v) => set("homeCity", joinCityState(v, splitCityState(a.homeCity).state))} />
        <TextField label="State" value={splitCityState(a.homeCity).state} placeholder="TN" suggestions={US_STATES}
                   onChange={(v) => set("homeCity", joinCityState(splitCityState(a.homeCity).city, v))} />
        <TextField label="Postal code" value={a.homePostalCode} placeholder="37221"
                   onChange={(v) => set("homePostalCode", v)} />
      </div>
      <TextField label="Country" value={a.homeCountry} placeholder="United States"
                 onChange={(v) => set("homeCountry", v)} />

      <h2>Work authorization</h2>
      <p className="muted small">
        Every application asks these two, and a wrong answer is a false statement
        on a legal document. They are typed by you and never inferred.
      </p>
      <YesNo
        label="Are you legally authorized to work in the United States?"
        value={a.workAuthorizedUs}
        onChange={(v) => set("workAuthorizedUs", v)}
      />
      <YesNo
        label="Will you now or in the future require visa sponsorship?"
        value={a.requiresSponsorship}
        onChange={(v) => set("requiresSponsorship", v)}
      />
      <RadioGroup label="Citizenship, for the forms that ask it by name" options={CITIZENSHIP}
                  value={a.citizenship}
                  onChange={(v) => set("citizenship", v)} />
      <p className="field-hint muted small">
        Optional. Answered, it fills “Citizenship status” lists and “Are you a U.S. citizen?”; it also lets a work-authorization
        list that asks the basis (“authorized due to my nationality”) be answered truthfully. Never inferred from the two questions above.
      </p>
      <RadioGroup label="Security clearance" options={CLEARANCE}
                  value={a.clearance}
                  onChange={(v) => set("clearance", v)} />
      <p className="field-hint muted small">
        Optional. Security roles ask “do you hold an active clearance?” and “have you ever held one?”; each is answered from this and nothing else.
        Which clearance, and eligibility questions, are always left for you.
      </p>

      <h2>Voluntary self-identification</h2>
      <p className="muted small">
        Optional, and it stays optional. Employers collect this for federal
        reporting and are prohibited from using it in hiring decisions. If you
        leave these blank we select “decline to answer” on the form — we never
        pick something else for you.
      </p>
      <RadioGroup label="Veteran status" options={VETERAN}
                  value={a.veteranStatus as string | null}
                  onChange={(v) => set("veteranStatus", v)} />
      <RadioGroup label="Disability status" options={DISABILITY}
                  value={a.disabilityStatus as string | null}
                  onChange={(v) => set("disabilityStatus", v)} />

      <h2>Where you work now</h2>
      <p className="muted small">
        Asked by name on most forms. Typed by you, once; left blank, the form's question is left for you.
      </p>
      <div className="pair">
        <TextField label="Current company" value={a.currentCompany} placeholder="Cat Financial"
                   onChange={(v) => set("currentCompany", v)} />
        <TextField label="Current title" value={a.currentTitle} placeholder="Test Manager"
                   onChange={(v) => set("currentTitle", v)} />
      </div>

      <h2>Consent boxes</h2>
      <p className="muted small">
        Nearly every form has a box for its privacy notice or terms, and Auto cannot send without it.
        JobCork ticks only the standard ones — privacy notice, terms of use, “the information I gave is accurate” —
        and never a background-check authorisation, an arbitration clause, a contract, a signature, or a marketing opt-in;
        those are always left for you.
      </p>
      <label className="choice">
        <input type="checkbox" checked={a.acceptStandardConsents} onChange={(e) => set("acceptStandardConsents", e.target.checked)} />
        <span>JobCork may tick employers' standard privacy and terms boxes on my behalf</span>
      </label>
      <p className="muted small">
        Some employers (Zscaler, for one) also ask you to acknowledge that what you learn in interviews is confidential — an NDA-style box, so it has its own switch.
        Arbitration agreements and background-check authorisations are never ticked for you.
      </p>
      <label className="choice">
        <input type="checkbox" checked={a.acceptConfidentiality} onChange={(e) => set("acceptConfidentiality", e.target.checked)} />
        <span>JobCork may also tick confidentiality / NDA acknowledgement boxes on my behalf</span>
      </label>
      <label className="choice">
        <input type="checkbox" checked={a.acceptAgreements} onChange={(e) => set("acceptAgreements", e.target.checked)} />
        <span>JobCork may accept application agreements for me — arbitration agreements, AI-use policies, codes of conduct — when a form asks as a Yes / I agree choice <span className="muted small">(these are legal agreements with the employer; read one before you turn this on. Never a background check, a non-compete, or anything about money.)</span></span>
      </label>

      <h2>Employers' own questions</h2>
      <p className="muted small">
        Many forms ask their own — “Why are you interested in working here?”, “Describe a recent project.” Everything above answers
        the standard questions; these need writing. When you open a posting yourself, JobCork drafts them from your resume for you to read
        before you press Submit. When JobCork sends without you — Auto, or “Send with JobCork” — it can write them too, with your permission:
        it sends a draft only when every claim in it is backed by your resume. A draft that says something your resume does not is sent back
        to the model once to have that taken out; one that still does comes back to you as “needs you”, with the reason. Questions in several
        parts, “what about us appeals to you”, and “what matters most to you in a role” are answered from the work on your record, never from
        invented wishes. Yes/no questions about your experience, and anything legal, are never answered this way.
      </p>
      <label className="choice">
        <input type="checkbox" checked={a.letModelAnswer} onChange={(e) => set("letModelAnswer", e.target.checked)} />
        <span>JobCork may answer employers' own written questions from my resume when it sends for me</span>
      </label>

      <h2>Your experience, for the screening questions</h2>
      <p className="muted small">
        “Do you have at least 3 years of experience as a Security Engineer or similar?”, “Experience working in healthcare?”, “Hands-on experience with Okta?”,
        “How many years in application security?” — the questions a resume never states in so many words. Stated here, they are answered from what you say
        and nothing else: a question that names one of these gets a yes (or the number of years), and the record says which fact. One the facts do not name is
        left for you; JobCork never answers “no” for you.
      </p>
      <TextField label="Years of professional experience, in total" type="number" value={a.yearsExperience === null ? "" : String(a.yearsExperience)}
                 hint="Answers “10+ years of professional experience?” and “how many years of experience do you have?” — the general ones only."
                 onChange={(v) => set("yearsExperience", v === "" ? null : Number(v))} />
      <AreasField values={a.experienceAreas} onChange={(v) => set("experienceAreas", v)} />
      <TagField label="Industries you have worked in"
                hint="Answers “experience working in healthcare?” and the like. Comma or Enter adds one."
                values={a.industries} onChange={(v) => set("industries", v)} placeholder="healthcare, finance, government" />
      <TagField label="Skills, tools and platforms you would say yes to"
                hint="Answers “hands-on experience with Okta?”, “experience with AWS, GCP or Azure?” (any one named), “threat modeling and/or architecture reviews?”. A question that joins two with “and” needs both here."
                values={a.skills} onChange={(v) => set("skills", v)} placeholder="AWS, Okta, threat modeling, incident response" />

      <h2>The rest</h2>
      <TextField label="Highest education" value={a.highestEducation}
                 placeholder="Master's degree"
                 onChange={(v) => set("highestEducation", v)} />
      <TextField label="Earliest start date" type="date" value={a.earliestStartDate}
                 onChange={(v) => set("earliestStartDate", v)} />
      <MoneyField label="Desired pay" cents={a.desiredCompCents}
                  hint="Base salary. Left blank, we skip the question wherever the form allows it."
                  onChange={(c) => set("desiredCompCents", c)} />
      <YesNo label="Are you willing to relocate?" value={a.willingToRelocate}
             onChange={(v) => set("willingToRelocate", v)} />
      <YesNo label="May recruiters text you (SMS / WhatsApp) about your applications?" value={a.smsOptIn}
             onChange={(v) => set("smsOptIn", v)} />
      <p className="field-hint muted small">
        Many forms ask. “Yes” ticks the opt-in or picks it; “No” leaves the box unticked or picks the no. Unanswered, the question is left for you.
      </p>
      <YesNo label="Are you open to some time in an office (hybrid) when a role asks for it?" value={a.openToOffice}
             onChange={(v) => set("openToOffice", v)} />
      <p className="field-hint muted small">
        “Are you open to working in-person in one of our offices 25% of the time?”, “open to a hybrid schedule?” — answered either way from here, as your own preference. A question that names a place (“from our Foster City, CA HQ 3 days a week?”) is always left for you.
      </p>
      <p className="field-hint muted small">
        Non-compete questions (“Are you subject to a non-compete or similar agreement?”) are answered “No” for everyone; they never hold up a send. If one does apply to you, it is something to raise at interview.
      </p>
      <TagField label="Companies you've interviewed with before"
                hint="“Have you ever interviewed at <company> before?” is answered Yes for these and for any interview JobCork has on record, and No otherwise."
                values={a.interviewedAt}
                onChange={(v) => set("interviewedAt", v)} />
      <TagField label="Companies you've worked for before"
                hint="Some applications ask whether you're a former employee. Listing them here lets us answer accurately."
                values={a.priorEmployeeOf}
                onChange={(v) => set("priorEmployeeOf", v)}
                placeholder="Acme Corp" />
      <YesNo label="Have you ever been convicted of a felony?" value={a.felonyConviction}
             onChange={(v) => set("felonyConviction", v)} />
      <p className="field-hint muted small">
        Answer “No” and Auto answers this for you on forms that ask it, so it stops leaving it for you.
        “Yes” or unanswered is always left for you to handle — Auto never answers “yes” in your name, and never guesses.
      </p>
      <TextField label="Disclosure, if any"
                 hint="If you answered yes above: what an application should be told, in your words. Left blank, we leave it blank."
                 value={a.felonyDisclosure}
                 onChange={(v) => set("felonyDisclosure", v)} />

      <QuestionBank />

      <ErrorList errors={errors} />

      {(state.missing.length > 0 || state.conflicts.length > 0) && (
        <>
          <h2>Still needed before we can apply</h2>
          <ul className="errlist">
            {state.missing.map((m) => <li key={m} className="small muted">{m}</li>)}
            {state.conflicts.map((c) => <li key={c} className="small err">{c}</li>)}
          </ul>
        </>
      )}

      <div className="row" id="answers-actions">
        <button className="btn" onClick={confirm}
                disabled={busy !== false || (!dirty && (!!a.confirmedAt || state.missing.length > 0 || state.conflicts.length > 0))}>
          {busy === "confirm" ? "Saving and confirming…" : a.confirmedAt && !dirty ? "Confirmed" : dirty && a.confirmedAt ? "Save and re-confirm" : "Save and confirm"}
        </button>
        <button className="btn ghost" onClick={save} disabled={busy !== false || !dirty}>
          {busy === "save" ? "Saving…" : "Save without confirming"}
        </button>
      </div>
      {(state.missing.length > 0 || state.conflicts.length > 0) && (
        <p className="muted small">{dirty ? "Save and confirm checks what you have entered; anything still needed is listed above afterwards." : "Confirming needs the items listed above first."}</p>
      )}
      {a.confirmedAt && !dirty && justConfirmed && (
        <p className="ok small">Confirmed — these answers go out under your name from now on.</p>
      )}
      {!a.confirmedAt && !dirty && state.missing.length === 0 && state.conflicts.length === 0 && (
        <p className="muted small">Saved, not yet confirmed. Nothing is sent on your behalf until you confirm.</p>
      )}
      <p className="muted small">
        Saving a change clears your confirmation. That is deliberate — it means
        we never submit an answer you have not seen in its current form.
      </p>

      <NextStep ready={!!a.confirmedAt && !dirty} next={next} onGo={onGo} onBack={onBack} doneWords="Answers confirmed" />
    </>
  );
}
